import type {
  MetaCarteirasFicFidcsChildPosition,
  MetaCarteirasFicFidcsChildrenMeta,
} from 'src/services/meta-carteiras-fic-fidcs';

export const CONCENTRATION_RANKS = [1, 5, 10, 25] as const;

export type ConcentrationRank = (typeof CONCENTRATION_RANKS)[number];

export type FicFidcConcentrationStatus =
  | 'ready'
  | 'empty'
  | 'partial'
  | 'unavailable';

export interface FicFidcConcentrationPoint {
  rank: number;
  fundName: string;
  value: number;
  accumulatedValue: number;
  accumulatedPercentage: number;
}

export interface FicFidcConcentrationData {
  status: FicFidcConcentrationStatus;
  totalFunds: number;
  totalValue: number | null;
  points: FicFidcConcentrationPoint[];
}

function toFiniteNumber(value: number | null | undefined): number | null {
  if (value === null || value === undefined) return null;

  const numericValue = Number(value);

  return Number.isFinite(numericValue) ? numericValue : null;
}

function getReportedFundCount(meta?: MetaCarteirasFicFidcsChildrenMeta) {
  const reportedFundCount = toFiniteNumber(meta?.total);

  if (
    reportedFundCount === null ||
    reportedFundCount < 0 ||
    !Number.isInteger(reportedFundCount)
  ) {
    return null;
  }

  return reportedFundCount;
}

function getValidPositionValue(position: MetaCarteirasFicFidcsChildPosition) {
  const value = toFiniteNumber(position.vlMercPosFinal);

  if (value === null || value < 0) return null;

  return value;
}

function buildPoints(
  positions: Array<{
    position: MetaCarteirasFicFidcsChildPosition;
    value: number;
  }>,
  totalValue: number,
) {
  let accumulatedValue = 0;

  return positions
    .sort((first, second) => second.value - first.value)
    .map(({ position, value }, index) => {
      accumulatedValue += value;

      return {
        rank: index + 1,
        fundName: position.name,
        value,
        accumulatedValue,
        accumulatedPercentage: (accumulatedValue / totalValue) * 100,
      };
    });
}

function emptyResult(
  status: FicFidcConcentrationStatus,
  totalFunds: number,
  totalValue: number | null,
): FicFidcConcentrationData {
  return { status, totalFunds, totalValue, points: [] };
}

/**
 * Builds the concentration curve from the already-loaded FIC child positions.
 * Null/invalid positions and inconsistent totals are kept out of the chart and
 * make the result partial instead of being silently converted to zero.
 */
export function mapFicFidcConcentration(
  positions: MetaCarteirasFicFidcsChildPosition[] = [],
  meta?: MetaCarteirasFicFidcsChildrenMeta,
): FicFidcConcentrationData {
  const reportedFundCount = getReportedFundCount(meta);
  const totalFunds = reportedFundCount ?? positions.length;
  const reportedTotalValue = toFiniteNumber(meta?.totalVlMercPosFinal);
  const hasInvalidFundCount =
    meta?.total !== undefined && reportedFundCount === null;
  const hasCountMismatch =
    reportedFundCount !== null && reportedFundCount !== positions.length;
  const validPositions: Array<{
    position: MetaCarteirasFicFidcsChildPosition;
    value: number;
  }> = [];
  let hasUnavailablePosition = false;

  positions.forEach((position) => {
    const value = getValidPositionValue(position);

    if (value === null) {
      hasUnavailablePosition = true;
      return;
    }

    validPositions.push({ position, value });
  });

  if (positions.length === 0) {
    if (hasInvalidFundCount || (reportedFundCount !== null && totalFunds > 0)) {
      return emptyResult('partial', totalFunds, reportedTotalValue);
    }

    if (reportedTotalValue !== null && reportedTotalValue > 0) {
      return emptyResult('unavailable', totalFunds, reportedTotalValue);
    }

    if (reportedTotalValue !== null && reportedTotalValue < 0) {
      return emptyResult('unavailable', totalFunds, reportedTotalValue);
    }

    return emptyResult('empty', totalFunds, reportedTotalValue);
  }

  if (hasInvalidFundCount || hasCountMismatch || hasUnavailablePosition) {
    return emptyResult('partial', totalFunds, reportedTotalValue);
  }

  const positionsTotal = validPositions.reduce(
    (sum, { value }) => sum + value,
    0,
  );

  const totalValue = reportedTotalValue ?? positionsTotal;

  if (totalValue < 0) {
    return emptyResult('unavailable', totalFunds, totalValue);
  }

  if (totalValue === 0) {
    return emptyResult('empty', totalFunds, totalValue);
  }

  if (positionsTotal <= 0) {
    return emptyResult('unavailable', totalFunds, totalValue);
  }

  const valueTolerance = Math.max(0.01, totalValue * 0.000001);

  if (Math.abs(positionsTotal - totalValue) > valueTolerance) {
    return emptyResult('partial', totalFunds, totalValue);
  }

  return {
    status: 'ready',
    totalFunds,
    totalValue,
    points: buildPoints(validPositions, totalValue),
  };
}

export function getConcentrationPointAtRank(
  data: FicFidcConcentrationData,
  rank: ConcentrationRank,
): FicFidcConcentrationPoint | null {
  if (data.status !== 'ready' || rank > data.totalFunds) return null;

  return data.points[rank - 1] ?? null;
}
